import type { Chip, PoiSummary } from '@linktivity/tata';
import { action, computed, observable, reaction } from 'mobx';
import type { POI } from '~/layouts/Base/types';
import { poisApi } from '~/services';
import {
  bookingSummaryToPoi,
  placePoisToPoiSummaries,
  poiSummaryToPoi
} from '~/utils/poi';
import { syncQueryParam } from '~/utils/state';
import booking from './booking';
import lang from './lang';

// When the first nearby-POIs request comes back empty the backend is still
// generating them, so we poll until results/chips appear or we time out.
const NEARBY_POLL_INTERVAL_MS = 3000;
const NEARBY_POLL_DURATION_MS = 60000;

const delay = (ms: number) =>
  new Promise<void>(resolve => setTimeout(resolve, ms));

export class PoiStore {
  @observable accessor nearbyPOIs: PoiSummary[] = [];
  @observable accessor chips: Chip[] = [];
  @observable accessor poiId = '';
  @observable accessor selectedPoi: POI | null = null;

  // Identifies the latest load() so stale responses (e.g. after a language
  // change) can be discarded instead of overwriting newer data.
  private loadSeq = 0;

  constructor() {
    // (Re)load nearby POIs whenever the booking's product or the active
    // language changes.
    reaction(
      () => ({
        productId: booking.booking?.activitySummary?.productId,
        languageCode: lang.languageCode
      }),
      () => this.load(),
      { fireImmediately: true }
    );

    // Mirror the poi id into the URL so the view can be shared and restored
    // across reloads.
    reaction(
      () => this.poiId,
      poiId => syncQueryParam('poiId', poiId)
    );

    // Only the id is in the URL, so resolve it back to a POI (and re-select
    // it) once the POIs are loaded.
    reaction(
      () => this.pois,
      pois => {
        if (!this.poiId || this.selectedPoi) return;
        const match = pois.find(poi => poi.id === this.poiId);
        if (match) this.focusPoi(match);
      }
    );
  }

  @computed
  get nearbyPois(): POI[] {
    return this.nearbyPOIs
      .map(poiSummaryToPoi)
      .filter((poi): poi is POI => poi !== null);
  }

  @computed
  get pois(): POI[] {
    const mainPOI = bookingSummaryToPoi(booking.booking);
    return mainPOI ? [mainPOI, ...this.nearbyPois] : this.nearbyPois;
  }

  // The chip texts, which the search box rolls through as its placeholder.
  @computed
  get chipTexts(): string[] {
    return this.chips
      .map(chip => chip.text)
      .filter((text): text is string => Boolean(text));
  }

  @action.bound
  setPoiId(poiId: string) {
    this.poiId = poiId;
  }

  // Pan/highlight the map to a POI — opening its summary sheet (and at which
  // position) is the page's concern.
  @action.bound
  focusPoi(target: POI) {
    this.selectedPoi = target;
    this.poiId = target.id;
  }

  @action.bound
  deselectPoi() {
    this.selectedPoi = null;
    this.poiId = '';
  }

  @action
  setNearbyPOIs(pois: PoiSummary[]) {
    this.nearbyPOIs = pois;
  }

  @action
  setChips(chips: Chip[]) {
    this.chips = chips;
  }

  // Drop the nearby POIs and invalidate any in-flight load/poll, so a
  // booking switch doesn't show (or re-merge) the previous booking's POIs
  // while the new ones load.
  @action.bound
  clearNearbyPOIs() {
    this.loadSeq++;
    this.nearbyPOIs = [];
    this.chips = [];
  }

  // Append POIs we don't already have, keyed by placeId, so polled results
  // accumulate without duplicates.
  @action
  mergeNearbyPOIs(pois: PoiSummary[]) {
    if (pois.length === 0) return;
    const seen = new Set(this.nearbyPOIs.map(poi => poi.place?.id));
    const additions = pois.filter(poi => !seen.has(poi.place?.id));
    if (additions.length === 0) return;
    this.nearbyPOIs = [...this.nearbyPOIs, ...additions];
  }

  @action
  async load() {
    const productId = booking.booking?.activitySummary?.productId;
    if (!productId) return;
    const { languageCode } = lang;
    const seq = ++this.loadSeq;

    // The chips are the search box's rolling placeholder, so they're loaded
    // alongside the POIs rather than only when the search page opens.
    this.loadChips(productId, languageCode, seq);

    try {
      const nearby = await poisApi.getNearbyPois({ productId, languageCode });
      if (seq !== this.loadSeq) return;
      const pois = placePoisToPoiSummaries(nearby.placePois);
      this.setNearbyPOIs(pois);

      // Got results on the first try — no need to poll.
      if (pois.length > 0) return;

      // Otherwise the backend is still generating; poll until ready.
      await this.pollNearby(productId, languageCode, seq);
    } catch (error) {
      console.error('Failed to load nearby POIs', error);
    }
  }

  // Poll nearby POIs every few seconds, merging any that arrive. Chips becoming
  // available signals generation finished, which ends the poll; otherwise we
  // stop after NEARBY_POLL_DURATION_MS. `seq` guards against a newer load()
  // (e.g. a language change) taking over mid-poll.
  private async pollNearby(
    productId: string,
    languageCode: typeof lang.languageCode,
    seq: number
  ) {
    const deadline = Date.now() + NEARBY_POLL_DURATION_MS;

    while (Date.now() < deadline) {
      await delay(NEARBY_POLL_INTERVAL_MS);
      if (Date.now() >= deadline) return;
      if (seq !== this.loadSeq) return;

      try {
        const nearby = await poisApi.getNearbyPois({ productId, languageCode });
        if (seq !== this.loadSeq) return;
        this.mergeNearbyPOIs(placePoisToPoiSummaries(nearby.placePois));
      } catch (error) {
        console.error('Failed to poll nearby POIs', error);
      }

      const chips = await this.loadChips(productId, languageCode, seq);
      if (seq !== this.loadSeq) return;
      if (chips.length > 0) return;
    }
  }

  // Returns the chips so pollNearby can use them as its "generation finished"
  // signal. Stale responses (a newer load() took over) are dropped.
  private async loadChips(
    productId: string,
    languageCode: typeof lang.languageCode,
    seq: number
  ): Promise<Chip[]> {
    try {
      const { placeChips } = await poisApi.getPoiSearchChips({
        productId,
        languageCode
      });
      if (seq !== this.loadSeq) return [];
      const chips = placeChips?.[0]?.chips ?? [];
      this.setChips(chips);
      return chips;
    } catch (error) {
      console.error('Failed to load POI search chips', error);
      return [];
    }
  }
}

export default new PoiStore();
